/**
 * @typedef {'facility.updated'|'facility.balance.changed'|'notification.created'|'dashboard.updated'|'transaction.updated'|'session.expired'|'rfq.bid.submitted'|'rfq.bid.updated'|'rfq.bid.withdrawn'|'rfq.bid.accepted'|'rfq.bid.confirmed'|'rfq.bid.declined'|'rfq.counter.created'|'rfq.counter.revised'|'rfq.counter.expired'|'rfq.bid.expired'|'rfq.bid.rank.changed'|'rfq.nudge.sent'|string} RealtimeEventType
 */

/**
 * @typedef {Object} RealtimeEnvelope
 * @property {RealtimeEventType} eventType
 * @property {string} timestamp
 * @property {string} correlationId
 * @property {number} sequence
 * @property {Record<string, unknown>} payload
 */

/**
 * @typedef {'idle'|'connecting'|'open'|'closed'|'reconnecting'} RealtimeStatus
 */

/**
 * @typedef {(envelope: RealtimeEnvelope) => void} RealtimeHandler
 */

export const REALTIME_EVENT_TYPES = Object.freeze({
  FACILITY_UPDATED: 'facility.updated',
  FACILITY_BALANCE_CHANGED: 'facility.balance.changed',
  NOTIFICATION_CREATED: 'notification.created',
  DASHBOARD_UPDATED: 'dashboard.updated',
  TRANSACTION_UPDATED: 'transaction.updated',
  SESSION_EXPIRED: 'session.expired',
  RFQ_BID_SUBMITTED: 'rfq.bid.submitted',
  RFQ_BID_UPDATED: 'rfq.bid.updated',
  RFQ_BID_WITHDRAWN: 'rfq.bid.withdrawn',
  RFQ_BID_ACCEPTED: 'rfq.bid.accepted',
  RFQ_BID_CONFIRMED: 'rfq.bid.confirmed',
  RFQ_BID_DECLINED: 'rfq.bid.declined',
  RFQ_COUNTER_CREATED: 'rfq.counter.created',
  RFQ_COUNTER_REVISED: 'rfq.counter.revised',
  RFQ_COUNTER_EXPIRED: 'rfq.counter.expired',
  RFQ_BID_EXPIRED: 'rfq.bid.expired',
  RFQ_BID_RANK_CHANGED: 'rfq.bid.rank.changed',
  RFQ_NUDGE_SENT: 'rfq.nudge.sent',
  // Legacy alias from early facility WS
  LEGACY_FACILITY_UPDATE: 'facility_update',
});

/**
 * @param {unknown} envelope
 * @returns {string}
 */
export function getEnvelopeRfqUlid(envelope) {
  const payload = envelope && typeof envelope === 'object' ? envelope.payload : null;
  if (!payload || typeof payload !== 'object') return '';
  const id = payload.rfqUlid || payload.rfq_ulid;
  return typeof id === 'string' ? id : '';
}

/**
 * @param {unknown} value
 * @returns {value is RealtimeEnvelope}
 */
export function isRealtimeEnvelope(value) {
  if (!value || typeof value !== 'object') return false;
  const v = /** @type {Record<string, unknown>} */ (value);
  return (
    typeof v.eventType === 'string' &&
    typeof v.timestamp === 'string' &&
    typeof v.correlationId === 'string' &&
    typeof v.sequence === 'number' &&
    v.payload !== null &&
    typeof v.payload === 'object'
  );
}

/**
 * Normalize legacy facility_update frames into the canonical envelope.
 * @param {unknown} raw
 * @returns {RealtimeEnvelope|null}
 */
export function normalizeIncomingMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (isRealtimeEnvelope(raw)) return raw;
  const v = /** @type {Record<string, unknown>} */ (raw);
  if (v.type === 'facility_update') {
    return {
      eventType: REALTIME_EVENT_TYPES.FACILITY_BALANCE_CHANGED,
      timestamp: new Date().toISOString(),
      correlationId: `legacy-${Date.now()}`,
      sequence: Date.now(),
      payload: {
        facilityUlid: v.facility_ulid,
        organizationUlid: v.organization_ulid,
        changed: { utilizedAmount: v.utilized_amount },
      },
    };
  }
  return null;
}
